/**
 * Which confirmation a Product Information upload is waiting for (BM-9211).
 *
 * The AddProducts Lambda stops before its first write when the sheet holds products that already
 * exist (duplicates) or would change an existing product's Colourway, sizes, Variable 1, Variable 2,
 * Category 1/2, Brand or Product Type. It reports that through the upload callback, and the global
 * socket handler opens the matching popup. Confirming re-runs the same S3 file with the answer.
 */

/** The popup a held upload needs, or null when the callback needs none. */
export type RangePlanUploadPrompt = 'duplicates' | 'keyFieldChanges' | null;

/** The fields of the upload callback this decision reads. */
export interface RangePlanUploadCallbackData {
  errorMsg?: string;
  duplicatesFound?: boolean;
  keyFieldChangesFound?: boolean;
  uploadKey?: string;
  file?: string;
  includeDuplicates?: boolean;
}

/** A key-field confirmation waiting for the user's answer. */
export interface PendingKeyFieldConfirmation {
  uploadKey: string;
  includeDuplicates: boolean;
  /** This run's own error sheet — it does not list duplicates the user had already approved. */
  file?: string;
  /** The error sheet of the duplicates popup the user approved for this same upload, if any. */
  duplicatesErrorSheet?: string;
}

/** The duplicates popup the user approved: which upload, and the error sheet it came with. */
export interface ApprovedDuplicates {
  uploadKey: string;
  file?: string;
}

/** The body `POST /product-range-plan/confirm-upload` takes for a key-field confirmation. */
export interface KeyFieldConfirmPayload {
  uploadKey: string;
  activeTab: 'product-info';
  includeDuplicates: boolean;
  confirmKeyFieldChanges: true;
}

/**
 * Decides which confirmation popup an upload callback opens.
 *
 * Duplicates come first — the Lambda asks about them before key-field changes — and nothing opens
 * without an upload key, because the upload could not be re-run from the popup.
 * @param data - The upload callback's `data`.
 * @returns The popup to open, or null.
 */
export const resolveUploadPrompt = (data: RangePlanUploadCallbackData | null | undefined): RangePlanUploadPrompt => {
  if (!data || data.errorMsg || !data.uploadKey) return null;
  if (data.duplicatesFound) return 'duplicates';
  if (data.keyFieldChangesFound) return 'keyFieldChanges';
  return null;
};

/**
 * Builds the re-run request for a confirmed key-field change.
 *
 * The duplicates decision is carried over as it was made: a sheet whose duplicates were already
 * confirmed re-runs with them, and a sheet that had none does not suddenly include them.
 * @param pending - The confirmation the user just accepted.
 * @returns The confirm-upload request body.
 */
export const buildKeyFieldConfirmPayload = (pending: PendingKeyFieldConfirmation): KeyFieldConfirmPayload => ({
  uploadKey: pending.uploadKey,
  activeTab: 'product-info',
  includeDuplicates: pending.includeDuplicates === true,
  confirmKeyFieldChanges: true,
});

/**
 * Builds the pending key-field confirmation for a held upload, carrying over the error sheet of the
 * duplicates popup the user approved for the SAME upload one step earlier.
 * @param held - The key-field callback's upload key, error sheet and duplicates decision.
 * @param approvedDuplicates - The last approved duplicates popup, if any.
 * @returns The confirmation to keep until the user answers.
 */
export const buildKeyFieldConfirmation = (
  held: { uploadKey: string; file?: string; includeDuplicates: boolean },
  approvedDuplicates: ApprovedDuplicates | null | undefined,
): PendingKeyFieldConfirmation => ({
  uploadKey: held.uploadKey,
  includeDuplicates: held.includeDuplicates,
  ...(held.file ? { file: held.file } : {}),
  ...(approvedDuplicates?.uploadKey === held.uploadKey && approvedDuplicates.file
    ? { duplicatesErrorSheet: approvedDuplicates.file }
    : {}),
});

/**
 * The error sheet to hand back when the user cancels the key-field popup.
 *
 * Cancelling stops the whole upload, so duplicates the user approved one step earlier are not
 * written either — the upload ends as though they had been rejected. The sheet is therefore the one
 * the duplicates rejection gives, which lists those rows; the confirmed run's own sheet does not,
 * because it included them. Without an approved duplicates step, the run's own sheet is returned.
 * @param pending - The key-field confirmation being cancelled.
 * @returns The signed URL of the sheet to download, or null when there is none.
 */
export const resolveCancelErrorSheet = (pending: PendingKeyFieldConfirmation | null | undefined): string | null =>
  pending?.duplicatesErrorSheet || pending?.file || null;

/** The part of `Storage` the approved-duplicates memory uses. */
export interface ApprovedDuplicatesStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** One key per upload, in storage every tab of the app shares. */
const APPROVED_DUPLICATES_PREFIX = 'plm:rpApprovedDuplicates:v1:';

/** How long the approved sheet is kept — the lifetime of its signed link. */
const APPROVED_DUPLICATES_TTL_MS = 60 * 60 * 1000;

/**
 * Remembers the duplicates popup the user approved, for every tab (BM-9211).
 *
 * Each upload result now shows in whichever tab the user is looking at, so the key-field popup that
 * follows an approved duplicates popup can open in a different tab — which must still be able to hand
 * back the duplicates sheet if the user cancels there.
 * @param storage - Storage shared by the app's tabs (`localStorage`).
 * @param approved - The upload and the sheet its duplicates popup carried.
 * @param now - The current time, in ms.
 */
export const rememberApprovedDuplicates = (storage: ApprovedDuplicatesStorage, approved: ApprovedDuplicates, now: number): void => {
  try {
    storage.setItem(
      `${APPROVED_DUPLICATES_PREFIX}${approved.uploadKey}`,
      JSON.stringify({ file: approved.file, expiresAt: now + APPROVED_DUPLICATES_TTL_MS }),
    );
  } catch {
    /** Storage blocked: the cancel then falls back to the run's own sheet. */
  }
};

/**
 * Reads the approved duplicates popup of one upload, whichever tab approved it.
 * @param storage - Storage shared by the app's tabs.
 * @param uploadKey - The upload asked about.
 * @param now - The current time, in ms.
 * @returns The approved popup, or null when none is remembered or it has expired.
 */
export const readApprovedDuplicates = (
  storage: ApprovedDuplicatesStorage,
  uploadKey: string,
  now: number,
): ApprovedDuplicates | null => {
  try {
    const raw = storage.getItem(`${APPROVED_DUPLICATES_PREFIX}${uploadKey}`);
    if (!raw) return null;
    const stored = JSON.parse(raw) as { file?: string; expiresAt?: number };
    if (!stored.expiresAt || stored.expiresAt <= now) return null;
    return stored.file ? { uploadKey, file: stored.file } : { uploadKey };
  } catch {
    return null;
  }
};

/**
 * Forgets the approved duplicates popup of one upload once its key-field popup is answered.
 * @param storage - Storage shared by the app's tabs.
 * @param uploadKey - The upload.
 */
export const forgetApprovedDuplicates = (storage: ApprovedDuplicatesStorage, uploadKey: string): void => {
  try {
    storage.removeItem(`${APPROVED_DUPLICATES_PREFIX}${uploadKey}`);
  } catch {
    /** Storage blocked: the entry expires on its own. */
  }
};

/** The duplicates popup on screen: its upload, its error sheet, and what else the same run found. */
export interface HeldDuplicates {
  uploadKey?: string;
  file?: string;
  keyFieldChangesFound?: boolean;
}

/**
 * The key-field popup to open straight after the user approves the duplicates popup, or null when that
 * approval alone lets the upload go ahead.
 *
 * The Lambda reports the key-field changes of the duplicate rows on the same run as the duplicates, so
 * the second question is asked at once, without re-running the upload in between. Confirming it re-runs
 * the upload once, with the duplicates included and the changes confirmed; cancelling it hands back
 * the duplicates sheet, because nothing at all is written.
 * @param held - The duplicates popup the user just approved.
 * @returns The key-field confirmation to open, or null to re-run the upload now.
 */
export const resolveKeyFieldConfirmationAfterDuplicates = (
  held: HeldDuplicates | null | undefined,
): PendingKeyFieldConfirmation | null => {
  if (!held?.uploadKey || held.keyFieldChangesFound !== true) return null;
  return buildKeyFieldConfirmation(
    { uploadKey: held.uploadKey, includeDuplicates: true },
    held.file ? { uploadKey: held.uploadKey, file: held.file } : { uploadKey: held.uploadKey },
  );
};

/**
 * Runs a confirm action at most once at a time, so a double click on a popup's Confirm sends the
 * confirm-upload request — and so the AddProducts Lambda run — only once.
 *
 * The lock is set before the first await, which also refuses a second click in the same tick, before
 * React has re-rendered the disabled button. It is always released when the action ends, and an error
 * the action throws is passed on to the caller unchanged after the release.
 * @param lock - The in-flight flag, e.g. a React `useRef(false)`.
 * @param action - The confirm work to run.
 * @param onBusyChange - Told true when the action starts and false when it ends, success or error.
 * @returns True when the action ran, false when an earlier run was still in flight.
 */
export const runSingleFlight = async (lock: { current: boolean }, action: () => Promise<void>, onBusyChange?: (busy: boolean) => void): Promise<boolean> => {
  if (lock.current) return false;
  lock.current = true;
  onBusyChange?.(true);
  try {
    await action();
  } finally {
    lock.current = false;
    onBusyChange?.(false);
  }
  return true;
};
